import { useState, useEffect, useCallback } from "react";

const DB_NAME = "XingheImagesDB";
const DB_VERSION = 1;
const STORE_NAME = "images";

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
  return dbPromise;
}

// ─── Core API ───────────────────────────────────────────────────────────────

export async function saveImageFile(key: string, file: File): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const req = store.put(file, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function loadImageFile(key: string): Promise<File | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(key);
    req.onsuccess = () => resolve((req.result as File) || null);
    req.onerror = () => reject(req.error);
  });
}

export async function deleteImageFile(key: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const req = store.delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function listImageKeys(): Promise<string[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAllKeys();
    req.onsuccess = () => resolve(req.result as string[]);
    req.onerror = () => reject(req.error);
  });
}

// ─── Hook for reading local image URL ───────────────────────────────────────

export function useLocalImage(key: string): string | null {
  const [url, setUrl] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const file = await loadImageFile(key);
    if (file) {
      setUrl(URL.createObjectURL(file));
    } else {
      setUrl(null);
    }
  }, [key]);

  useEffect(() => {
    refresh();
    // Listen for specific image update events
    const handler = () => refresh();
    window.addEventListener(`site-image-updated-${key}`, handler);
    // Also refresh when any content update happens (covers URL changes)
    window.addEventListener("site-content-updated", handler);
    return () => {
      window.removeEventListener(`site-image-updated-${key}`, handler);
      window.removeEventListener("site-content-updated", handler);
    };
  }, [key, refresh]);

  return url;
}

// ─── Helper: get image with fallback ──────────────────────────────────────
// fallbackUrl is tracked as a ref so changes are always reflected immediately

export function useImageWithFallback(key: string, fallbackUrl: string): string {
  const localUrl = useLocalImage(key);
  // Always use the latest fallbackUrl — if localUrl exists it takes priority,
  // otherwise return the current fallbackUrl (which may have just been updated
  // by useReadContent responding to a content-updated event).
  return localUrl ?? fallbackUrl;
}

// ─── Broadcast image update ─────────────────────────────────────────────────

export function broadcastImageUpdate(key: string): void {
  window.dispatchEvent(new CustomEvent(`site-image-updated-${key}`));
}